// frontend/src/app/components/onboarding/stage2-framework-generation.tsx
//
// Stage 2 (#136): the framework generated in steps from the brand's own site
// read and documents (backend app/services/framework_generation). The screen
// gathers the inputs (category, vertical, brief, site read, documents, pasted
// text), starts a generation on the worker, polls it, and shows each step's
// newest version: passed, failed with the validator's problems, or stale
// because what it was built from was regenerated. Any one step can be
// regenerated without touching the others.

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronRight, Eye, Loader2, RotateCcw, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";

import {
  GENERATION_STEPS, VERTICAL_OPTIONS,
  getCustomer, getFrameworkGeneration, getFrameworkGenerationStep, runStage2Framework, updateCustomerProfile,
  type CustomerDetail, type FrameworkGenerationOverview, type GenerationStepDetail, type GenerationStepKey,
} from "@/api/onboarding-api";
import { setVertical, uploadTruthDocument } from "@/api/truth-set";
import { SiteReadPanel } from "@/app/components/truth-set/site-read-panel";
import { StepDetail } from "./stage2-step-detail";

const POLL_MS = 4000;
const COUNT_KEYS = ["beliefs", "personas", "questions_per_form"] as const;
const COUNT_LABELS: Record<(typeof COUNT_KEYS)[number], string> = {
  beliefs: "beliefs",
  personas: "personas",
  questions_per_form: "questions per form",
};
type CountKey = (typeof COUNT_KEYS)[number];

const card: React.CSSProperties = {
  backgroundColor: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 20,
};
const input: React.CSSProperties = {
  width: "100%", padding: "8px 12px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 8, outline: "none",
  fontFamily: "inherit",
};
const label: React.CSSProperties = {
  display: "block", fontSize: "var(--text-xs)", fontWeight: 600, textTransform: "uppercase",
  letterSpacing: "0.05em", color: "var(--muted-foreground)", marginBottom: 6,
};
const muted: React.CSSProperties = { color: "var(--muted-foreground)", fontSize: "var(--text-xs)" };
const btn = (primary = false, disabled = false): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: primary ? "8px 16px" : "5px 12px",
  borderRadius: 8, fontSize: primary ? "var(--text-sm)" : "var(--text-xs)", fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
  border: primary ? "none" : "1px solid var(--border)",
  backgroundColor: primary ? "var(--alpha-teal)" : "transparent",
  color: primary ? "white" : "var(--foreground)",
});

const isLive = (overview: FrameworkGenerationOverview | null) =>
  !!overview?.generation && (overview.generation.status === "pending" || overview.generation.status === "running");

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={card}>
      <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>{title}</h3>
      {subtitle && <p style={{ ...muted, margin: "2px 0 14px 0" }}>{subtitle}</p>}
      {!subtitle && <div style={{ height: 12 }} />}
      {children}
    </div>
  );
}

export function Stage2FrameworkGeneration({
  customer,
  onCustomerUpdate,
  onStageComplete,
}: {
  customer: CustomerDetail;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onStageComplete: () => void;
}) {
  const [overview, setOverview] = useState<FrameworkGenerationOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<CountKey, number> | null>(null);
  const [category, setCategory] = useState(customer.category ?? "");
  const [brief, setBrief] = useState(customer.brief ?? "");
  const [savingInput, setSavingInput] = useState<string | null>(null);
  const [pasted, setPasted] = useState("");
  const [openStep, setOpenStep] = useState<GenerationStepDetail | null>(null);
  const wasLive = useRef(false);
  // The parent recreates this callback on every render; read it through a ref
  // so `load` (and the effects keyed on it) stay stable.
  const onUpdate = useRef(onCustomerUpdate);
  onUpdate.current = onCustomerUpdate;

  const load = useCallback(async () => {
    try {
      const next = await getFrameworkGeneration(customer.id);
      setOverview(next);
      setLoadError(null);
      setCounts((prev) => prev ?? next.counts);
      // A generation that just finished changed framework_json: refresh the
      // customer so Stage 3 opens on it.
      if (wasLive.current && !isLive(next)) {
        const fresh = await getCustomer(customer.id);
        onUpdate.current(fresh);
        if (next.generation?.status === "completed") toast.success("Framework generated. Review it on Stage 3.");
        else if (next.generation?.error) toast.error(next.generation.error);
      }
      wasLive.current = isLive(next);
    } catch (e: any) {
      setLoadError(e?.message || "Could not load the generation");
    }
  }, [customer.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isLive(overview)) return;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [overview, load]);

  const start = async (steps?: GenerationStepKey[]) => {
    const key = steps ? steps.join(",") : "all";
    setStarting(key);
    try {
      await runStage2Framework(customer.id, { steps, counts: counts ?? undefined });
      wasLive.current = true;
      toast.success(steps ? "Regenerating that step." : "Framework generation started.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Could not start the generation");
    } finally {
      setStarting(null);
    }
  };

  const saveProfile = async (field: "category" | "brief", value: string) => {
    setSavingInput(field);
    try {
      const res = await updateCustomerProfile(customer.id, { [field]: value }, "Stage 2 input");
      onCustomerUpdate(res.customer);
      toast.success(field === "category" ? "Category saved." : "Brief saved.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSavingInput(null);
    }
  };

  const saveVertical = async (value: string) => {
    setSavingInput("vertical");
    try {
      await setVertical(customer.id, value);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSavingInput(null);
    }
  };

  const addPasted = async () => {
    const text = pasted.trim();
    if (!text) return;
    setSavingInput("pasted");
    try {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      await uploadTruthDocument(customer.id, new File([text], `pasted-text-${stamp}.txt`, { type: "text/plain" }));
      setPasted("");
      toast.success("Text added as a document.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Could not add the text");
    } finally {
      setSavingInput(null);
    }
  };

  const viewStep = async (stepId: number | null) => {
    if (!stepId) return;
    try {
      setOpenStep(await getFrameworkGenerationStep(customer.id, stepId));
    } catch (e: any) {
      toast.error(e?.message || "Could not load the step");
    }
  };

  if (loadError && !overview) {
    return <Section title="Framework generation"><p style={{ ...muted, color: "var(--infrared-coral)" }}>{loadError}</p></Section>;
  }
  if (!overview) {
    return <Section title="Framework generation"><Loader2 className="w-4 h-4 animate-spin" /></Section>;
  }

  const locked = !!customer.framework_approved;
  const live = isLive(overview);
  const inputs = overview.inputs;
  const missing: string[] = [];
  if (!inputs.category) missing.push("a category");
  if (!inputs.vertical) missing.push("a vertical");
  if (inputs.pages + inputs.documents === 0) missing.push("a site read or a document");
  const canStart = overview.available && !locked && !live && missing.length === 0 && !starting;
  const anyPassed = GENERATION_STEPS.some((s) => overview.steps[s]?.passed_version);

  return (
    <div className="space-y-5">
      <Section title="Inputs" subtitle="What the framework is generated from. No web search: only the brand's own site and documents.">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <span style={label}>Category *</span>
            <div className="flex gap-2">
              <input value={category} onChange={(e) => setCategory(e.target.value)} style={input}
                placeholder="What the brand is, as its buyers would say it" disabled={locked} />
              <button style={btn(false, locked || savingInput === "category" || category.trim() === inputs.category)}
                disabled={locked || savingInput === "category" || category.trim() === inputs.category || !category.trim()}
                onClick={() => saveProfile("category", category.trim())}>Save</button>
            </div>
          </div>
          <div>
            <span style={label}>Vertical *</span>
            <select value={inputs.vertical || ""} onChange={(e) => saveVertical(e.target.value)} style={input}
              disabled={locked || savingInput === "vertical"}>
              <option value="" disabled>Choose…</option>
              {VERTICAL_OPTIONS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <span style={label}>Brief (optional): what the brand wants to be recommended for, in the client&apos;s words</span>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
              style={{ ...input, resize: "vertical" }} disabled={locked} />
            <div className="flex justify-end mt-2">
              <button style={btn(false, locked || savingInput === "brief" || brief.trim() === inputs.brief)}
                disabled={locked || savingInput === "brief" || brief.trim() === inputs.brief}
                onClick={() => saveProfile("brief", brief.trim())}>Save brief</button>
            </div>
          </div>
        </div>
        <p style={{ ...muted, marginTop: 8 }}>
          Competitors: {inputs.competitors.length ? inputs.competitors.join(", ") : "none listed"} (edit at Stage 1).
          Sources: {inputs.pages} page{inputs.pages === 1 ? "" : "s"} from the site read, {inputs.documents} document
          {inputs.documents === 1 ? "" : "s"}.
        </p>
      </Section>

      <SiteReadPanel customerId={customer.id} onSourcesChanged={load} />

      <Section title="Paste text" subtitle="Anything the client sent that is not a file: added as a document the generation reads.">
        <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={4}
          style={{ ...input, resize: "vertical" }} placeholder="Paste text here" />
        <div className="flex justify-end mt-2">
          <button style={btn(false, !pasted.trim() || savingInput === "pasted")}
            disabled={!pasted.trim() || savingInput === "pasted"} onClick={addPasted}>
            <Upload className="w-3.5 h-3.5" /> Add as document
          </button>
        </div>
      </Section>

      <Section title="Generate"
        subtitle={`On ${overview.model}, in steps. Each step is validated and retried once with the problems; a step that still fails is shown as failed and never becomes part of the framework.`}>
        {!overview.available && (
          <p style={{ ...muted, color: "var(--infrared-coral)" }}>
            Framework generation is not available until db/migrations/2026-10-28_framework_generation.sql is applied.
          </p>
        )}
        {locked && (
          <p style={muted}>The framework is approved and locked. Unlock it at Stage 3 (with a reason) to regenerate.</p>
        )}
        {missing.length > 0 && !locked && (
          <p style={{ ...muted, color: "var(--signal-amber, var(--foreground))" }}>
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Add {missing.join(", ")} first.
          </p>
        )}
        <div className="flex items-end gap-3 flex-wrap mt-2">
          {COUNT_KEYS.map((key) => (
            <div key={key} style={{ width: 120 }}>
              <span style={label}>{COUNT_LABELS[key]}</span>
              <input type="number" style={input} min={overview.limits[key]?.min} max={overview.limits[key]?.max}
                value={counts?.[key] ?? overview.counts[key]} disabled={live || locked}
                onChange={(e) => setCounts({ ...(counts ?? overview.counts), [key]: Number(e.target.value) })} />
            </div>
          ))}
          <button style={btn(true, !canStart)} disabled={!canStart} onClick={() => start()}>
            {starting === "all" || live ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {live ? "Generating…" : anyPassed ? "Regenerate everything" : "Generate framework"}
          </button>
        </div>
        {overview.generation && (
          <p style={{ ...muted, marginTop: 10 }}>
            Last generation #{overview.generation.id}: <strong>{overview.generation.status}</strong>
            {overview.generation.steps.length < GENERATION_STEPS.length && ` (${overview.generation.steps.join(", ")})`}
            {` · $${Number(overview.generation.cost_usd || 0).toFixed(2)}`}
            {` · ${overview.library?.length
              ? `started from ${overview.library.length} framework library template${overview.library.length === 1 ? "" : "s"} (${overview.library.map((t) => `#${t.template_id}`).join(", ")})`
              : "no framework library template for this category yet"}`}
            {overview.generation.error && <span style={{ color: "var(--infrared-coral)" }}> · {overview.generation.error}</span>}
          </p>
        )}

        <div className="mt-4 space-y-2">
          {GENERATION_STEPS.map((step) => {
            const state = overview.steps[step];
            const failed = state?.latest_status === "failed";
            const stale = (state?.stale_on?.length ?? 0) > 0;
            const running = live && overview.generation?.steps.includes(step);
            return (
              <div key={step} className="p-3 rounded-lg" style={{ border: "1px solid var(--border)", backgroundColor: "var(--background)" }}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, minWidth: 210 }}>{state?.label ?? step}</span>
                  <span style={{ ...muted, flex: 1 }}>
                    {running ? "running…" : !state?.latest_version ? "not generated" : failed
                      ? `v${state.latest_version} failed validation${state.passed_version ? ` (v${state.passed_version} is in use)` : ""}`
                      : `v${state.passed_version} passed`}
                    {stale && !running && (
                      <span style={{ color: "var(--signal-amber, var(--foreground))" }}>
                        {" "}· stale: built from an older {state!.stale_on.join(", ")}
                      </span>
                    )}
                  </span>
                  {!failed && state?.passed_version && !stale && <Check className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />}
                  <button style={btn(false, !state?.latest_step_id)} disabled={!state?.latest_step_id}
                    onClick={() => viewStep(state?.latest_step_id ?? null)}>
                    <Eye className="w-3.5 h-3.5" /> Prompt & output
                  </button>
                  <button style={btn(false, !canStart || !anyPassed)} disabled={!canStart || !anyPassed}
                    onClick={() => start([step])} title="Regenerate this step only; the others are left as they are">
                    {starting === step ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Regenerate
                  </button>
                </div>
                {failed && state && state.latest_problems.length > 0 && (
                  <ul style={{ ...muted, color: "var(--infrared-coral)", margin: "8px 0 0 16px", listStyle: "disc" }}>
                    {state.latest_problems.slice(0, 8).map((p, i) => <li key={i}>{p.message}</li>)}
                    {state.latest_problems.length > 8 && <li>and {state.latest_problems.length - 8} more</li>}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {overview.generated && overview.framework_problems.length > 0 && (
          <p style={{ ...muted, color: "var(--signal-amber, var(--foreground))", marginTop: 12 }}>
            The current framework has {overview.framework_problems.length} validation problem
            {overview.framework_problems.length === 1 ? "" : "s"} (approval is blocked until they are fixed):{" "}
            {overview.framework_problems[0].message}
          </p>
        )}
        {overview.generated && !live && (
          <div className="flex justify-end mt-4">
            <button style={btn(true)} onClick={onStageComplete}>
              Review on Stage 3 <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </Section>

      {openStep && <StepDetail detail={openStep} framework={customer.framework} onClose={() => setOpenStep(null)} />}
    </div>
  );
}
